import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const CurrencyContext = createContext(null);

const OVERRIDE_KEY = "gaina_currency_override"; // user manual toggle

const SYMBOLS = { INR: "\u20b9", USD: "$", EUR: "\u20ac", GBP: "\u00a3" };

/**
 * Tiered INR → USD pricing brackets (fallback for USD/EUR/GBP when no override):
 */
function inrToUsdTier(inrRaw) {
  const inr = Number(inrRaw) || 0;
  if (inr <= 0) return 0;
  if (inr < 1000) return Math.max(1, Math.round(inr * 0.06));
  if (inr < 2000) return 70;
  if (inr < 3000) return Math.round(100 + ((inr - 2000) / 1000) * (125 - 100));
  if (inr < 4000) return Math.round(125 + ((inr - 3000) / 1000) * (150 - 125));
  if (inr <= 5000) return Math.round(180 + ((inr - 4000) / 1000) * (220 - 180));
  return Math.round(220 + ((inr - 5000) / 200) * 8);
}

export function CurrencyProvider({ children }) {
  const [country, setCountry] = useState("IN");
  const [isIndia, setIsIndia] = useState(true);
  const [allowedCurrencies, setAllowedCurrencies] = useState(["INR"]);
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem(OVERRIDE_KEY) || "INR";
  });
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/geo");
        setCountry(data.country || "IN");
        const isIN = !!data.is_india;
        setIsIndia(isIN);
        const allowed = data.allowed_currencies && data.allowed_currencies.length
          ? data.allowed_currencies
          : (isIN ? ["INR"] : ["USD", "EUR", "GBP"]);
        setAllowedCurrencies(allowed);
        // Enforce: if stored override is INR but user is outside India → drop it
        const stored = localStorage.getItem(OVERRIDE_KEY);
        if (!stored || !allowed.includes(stored)) {
          const initial = isIN ? "INR" : "USD";
          setCurrency(initial);
          localStorage.setItem(OVERRIDE_KEY, initial);
        }
      } catch (e) { /* keep default */ }
      setDetected(true);
    })();
  }, []);

  const setCurrencyManual = (c) => {
    const norm = allowedCurrencies.includes(c) ? c : allowedCurrencies[0];
    setCurrency(norm);
    localStorage.setItem(OVERRIDE_KEY, norm);
  };

  const symbol = (c) => SYMBOLS[c || currency] || "\u20b9";

  // Convert an INR amount to the target currency (USD/EUR/GBP use tier fallback)
  const convertFromInr = (inrRaw, target = currency) => {
    const inr = Number(inrRaw) || 0;
    if (target === "INR") return Math.round(inr);
    return inrToUsdTier(inr); // EUR/GBP fall back to same tier when no override
  };

  // A book is "Price on Request" when its INR base price is missing or 0.
  // Every downstream helper (format, formatBook, formatBookDual) short-circuits
  // to a friendly label so we never render ₹0 / $0 anywhere on the site.
  const isPriceOnRequest = (book, field = "price") => {
    if (!book) return false;
    const inr = Number(book[field] || 0);
    if (inr > 0) return false;
    // If admin set a USD/EUR/GBP override > 0, the book DOES have a price.
    for (const c of ["usd", "eur", "gbp"]) {
      const v = Number(book[`${field}_${c}_override`] || 0);
      if (v > 0) return false;
    }
    if (Number(book[`${field}_usd`] || 0) > 0) return false;
    return true;
  };

  const PRICE_ON_REQUEST_LABEL = "Price on Request";

  // Primary format helper — supports legacy (inr, usd) signature AND book object.
  const format = (inrOrBook, usdMaybe) => {
    if (inrOrBook && typeof inrOrBook === "object") {
      return formatBook(inrOrBook, "price");
    }
    const inr = Number(inrOrBook) || 0;
    if (inr <= 0 && (usdMaybe === undefined || Number(usdMaybe) <= 0)) return PRICE_ON_REQUEST_LABEL;
    if (currency === "INR") return `${symbol()}${Math.round(inr)}`;
    // For USD/EUR/GBP, if a legacy USD value is passed, use it; else convert
    const converted = (usdMaybe !== undefined && usdMaybe !== null) ? Number(usdMaybe) : convertFromInr(inr);
    if (converted <= 0) return PRICE_ON_REQUEST_LABEL;
    return `${symbol()}${converted}`;
  };

  // formatBook(book, field) — honors admin's currency override if set
  const formatBook = (book, field = "price") => {
    if (!book) return "";
    if (field === "price" && isPriceOnRequest(book, "price")) return PRICE_ON_REQUEST_LABEL;
    const inrVal = Number(book[field] || 0);
    if (currency === "INR") {
      if (inrVal <= 0) return field === "price" ? PRICE_ON_REQUEST_LABEL : "";
      return `${symbol()}${Math.round(inrVal)}`;
    }

    // Look up manual override for the current currency
    const overrideKey = `${field}_${currency.toLowerCase()}_override`;
    const override = book[overrideKey];
    if (override !== undefined && override !== null && override !== "" && Number(override) > 0) {
      return `${symbol()}${Number(override)}`;
    }
    // Backend already computes USD (blends override + tier). Use it for USD; tier for EUR/GBP
    if (currency === "USD") {
      const backendUsd = book[`${field}_usd`];
      if (backendUsd !== undefined && backendUsd !== null && Number(backendUsd) > 0) {
        return `${symbol()}${Math.round(Number(backendUsd))}`;
      }
    }
    const converted = convertFromInr(inrVal);
    if (converted <= 0) return field === "price" ? PRICE_ON_REQUEST_LABEL : "";
    return `${symbol()}${converted}`;
  };

  // Unit price (numeric) for cart/checkout math
  const unitPrice = (book) => {
    if (!book) return 0;
    if (currency === "INR") return Number(book.price || 0);
    const overrideKey = `price_${currency.toLowerCase()}_override`;
    const override = book[overrideKey];
    if (override !== undefined && override !== null && override !== "" && Number(override) > 0) {
      return Number(override);
    }
    if (currency === "USD" && book.price_usd) return Number(book.price_usd);
    return convertFromInr(Number(book.price || 0));
  };

  // Unit MRP (numeric) — returns 0 if MRP <= price (no discount to show)
  const unitMrp = (book) => {
    if (!book || !book.mrp) return 0;
    if (currency === "INR") return Number(book.mrp || 0);
    const overrideKey = `mrp_${currency.toLowerCase()}_override`;
    const override = book[overrideKey];
    if (override !== undefined && override !== null && override !== "" && Number(override) > 0) {
      return Number(override);
    }
    if (currency === "USD" && book.mrp_usd) return Number(book.mrp_usd);
    return convertFromInr(Number(book.mrp || 0));
  };

  // Returns { primary, secondary } — strict single-currency display per geo.
  //   India → primary = INR only, secondary = null
  //   Non-India → primary = USD only, secondary = null
  // (User requested no dual display anywhere — INR inside India, USD outside.)
  const formatBookDual = (book, field = "price") => {
    if (!book) return { primary: "", secondary: null };
    return { primary: formatBook(book, field), secondary: null };
  };

  const getOverrideBadges = () => [];

  return (
    <CurrencyContext.Provider value={{
      country, isIndia, currency, detected, allowedCurrencies,
      setCurrencyManual, symbol, format, formatBook, formatBookDual, unitPrice, unitMrp, getOverrideBadges,
      isPriceOnRequest,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
};
